import { useCallback, useMemo } from 'react'
import { useRouter } from 'next/router'

export type QueryParam = string | string[] | undefined

type QueryParamsRecord = Record<string, QueryParam>

type UseListQueryParamsOptions<TParams extends object> = {
  parse: (_query: QueryParamsRecord) => TParams
  serialize: (_params: TParams) => Record<string, string>
}

type UpdateOptions = {
  resetPage?: boolean
}

const getDynamicRouteParamKeys = (pathname: string) => {
  return pathname
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.startsWith('[') && segment.endsWith(']'))
    .map((segment) => segment.replace(/^\[+|\]+$/g, '').replace('...', ''))
    .filter(Boolean)
}

export const getQueryParam = (value: QueryParam) => {
  if (Array.isArray(value)) return value[0]
  return value
}

export const parseIntQueryParam = (value: QueryParam) => {
  const parsed = parseInt(getQueryParam(value) || '', 10)
  return Number.isNaN(parsed) ? undefined : parsed
}

export const parseBooleanQueryParam = (value: QueryParam) => {
  const normalized = getQueryParam(value)?.toLowerCase()

  if (normalized === 'true' || normalized === '1') return true
  if (normalized === 'false' || normalized === '0') return false

  return undefined
}

export const useListQueryParams = <TParams extends object>({
  parse,
  serialize
}: UseListQueryParamsOptions<TParams>) => {
  const router = useRouter()

  const params = useMemo(() => parse(router.query as QueryParamsRecord), [parse, router.query])

  const routeParams = useMemo(() => {
    const dynamicKeys = getDynamicRouteParamKeys(router.pathname)

    return dynamicKeys.reduce<Record<string, string | string[]>>((acc, key) => {
      const value = router.query[key]

      if (typeof value === 'string' || Array.isArray(value)) {
        acc[key] = value
      }

      return acc
    }, {})
  }, [router.pathname, router.query])

  const setParams = useCallback(
    (updates: Partial<TParams>, options?: UpdateOptions) => {
      const resetPage = options?.resetPage ?? true
      const nextParams = { ...params, ...updates } as TParams & { page?: unknown }

      if (resetPage && !('page' in updates) && 'page' in nextParams) {
        nextParams.page = 1
      }

      router.push(
        {
          pathname: router.pathname,
          query: {
            ...routeParams,
            ...serialize(nextParams as TParams)
          }
        },
        undefined,
        { shallow: true }
      )
    },
    [params, routeParams, router, serialize]
  )

  const setParam = useCallback(
    <K extends keyof TParams>(key: K, value: TParams[K], options?: UpdateOptions) => {
      setParams({ [key]: value } as unknown as Partial<TParams>, options)
    },
    [setParams]
  )

  return {
    params,
    setParams,
    setParam
  }
}
